import { Link } from "wouter";
import type { Lead } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export function AddNoteDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: Lead[];
  isLoadingLeads: boolean;
  query: string;
  onQueryChange: (value: string) => void;
  selectedLead: Lead | null;
  onSelectLead: (lead: Lead) => void;
  content: string;
  onContentChange: (value: string) => void;
  onSubmit: () => void;
}) {
  const filteredLeads = (() => {
    const needle = props.query.trim().toLowerCase();
    if (!needle) return props.leads;
    return props.leads.filter((l) => (l.name ?? "").toLowerCase().includes(needle) || l.phone.toLowerCase().includes(needle));
  })();

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Note</DialogTitle>
          <DialogDescription>Pick a chat, then add an internal note.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Chat</Label>
            <Input
              placeholder="Search name or phone..."
              value={props.query}
              onChange={(e) => props.onQueryChange(e.target.value)}
            />
            <div className="border rounded-md overflow-hidden">
              <ScrollArea className="h-56">
                <div className="p-2 space-y-1">
                  {props.isLoadingLeads ? (
                    <div className="p-2 text-sm text-muted-foreground">Loading chats...</div>
                  ) : filteredLeads.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No matches.</div>
                  ) : (
                    filteredLeads.map((l) => {
                      const active = props.selectedLead?.id === l.id;
                      return (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => props.onSelectLead(l)}
                          className={cn(
                            "w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors",
                            active ? "bg-muted" : ""
                          )}
                        >
                          <div className="text-sm font-medium truncate">{l.name || l.phone}</div>
                          <div className="text-xs text-muted-foreground truncate">{l.name ? l.phone : ""}</div>
                        </button>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
            {props.selectedLead ? (
              <div className="text-xs text-muted-foreground">
                Selected:{" "}
                <Link href={`/chats/${props.selectedLead.id}`} className="underline">
                  {props.selectedLead.name || props.selectedLead.phone}
                </Link>
              </div>
            ) : null}
          </div>

          <div className="grid gap-2">
            <Label>Note</Label>
            <Textarea
              placeholder="Add a note..."
              value={props.content}
              onChange={(e) => props.onContentChange(e.target.value)}
              className="min-h-[120px]"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => props.onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={props.onSubmit} disabled={!props.selectedLead || !props.content.trim()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

